import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Platform, Pressable, RefreshControl, ScrollView, TextInput, View } from 'react-native';
import { useFocusEffect, useLocalSearchParams, useRouter } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '@/context/ThemeContext';
import { useAuthStore, useChatInputStore, useCollectionStore, useFileStore, useLightboxStore, useModalStore, useToastStore } from '@/store';
import { useHasUser } from '@/hooks/useRequireAuth';
import useResponsiveness from '@/hooks/useResponsiveness';
import useFilesLibrary from '@/hooks/useFilesLibrary';
import useFilesAutoLoad from '@/hooks/useFilesAutoLoad';
import { useTranslation } from '@/hooks/useTranslation';
import { useModalHelper } from '@/hooks/useModalHelper';
import MetaHead from '@/components/MetaHead';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import CatalogSortDropdown from '@/components/Catalog/CatalogSortDropdown';
import LibraryFileItem, { FILE_CATEGORIES } from '@/components/Files/LibraryFileItem';
import { getLibraryPalette } from '@/components/Files/libraryPalette';
import AppDashboardFooter from '@/components/AppDashboardFooter';
import { downloadMedia } from '@/constants/utils/mediaDownload';
import { isCodeFile, isEditableDocumentFile, isEditorImportableFile, isLightboxCompatibleFile, isPdfFile, isPresentationFile, isSpreadsheetFile, openMessageFile } from '@/constants/utils/files';

const SORT_OPTIONS = [
  { id: 'newest', label: 'Newest first', icon: 'arrow-down-wide-short' },
  { id: 'oldest', label: 'Oldest first', icon: 'arrow-up-wide-short' },
  { id: 'a-z', label: 'Name A–Z', icon: 'arrow-down-a-z' },
  { id: 'z-a', label: 'Name Z–A', icon: 'arrow-up-z-a' },
];
const SOURCE_OPTIONS = [
  { id: 'all', label: 'All sources', icon: 'files' },
  { id: 'upload', label: 'Uploaded by you', icon: 'cloud-arrow-up' },
  { id: 'inference', label: 'AI generated', icon: 'sparkles' },
];

function Action({ label, icon, onPress, primary, danger, disabled, compact, quiet }) {
  const { colors, theme } = useTheme();
  const palette = getLibraryPalette(theme);
  const color = primary ? palette.onInk : danger ? colors.color_destructive : colors.text_color_primary;
  return <Pressable accessibilityRole="button" accessibilityLabel={label} accessibilityState={{ disabled: !!disabled }} disabled={disabled} onPress={onPress}
    style={({ hovered, pressed }) => ({ minHeight: 40, paddingHorizontal: compact ? 12 : 16, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, borderRadius: 10, borderWidth: 1, borderColor: primary ? palette.ink : quiet ? 'transparent' : palette.border, backgroundColor: primary ? palette.ink : hovered || pressed ? palette.hover : quiet ? 'transparent' : palette.surface, opacity: disabled ? 0.5 : primary && (hovered || pressed) ? 0.8 : 1 })}>
    {icon && <Icon name={icon} size={14} color={color} />}
    <Text size="s" weight="medium" style={{ color }}>{label}</Text>
  </Pressable>;
}

function EmptyResult({ title, description, icon = 'search', action, onAction }) {
  const { theme } = useTheme();
  const palette = getLibraryPalette(theme);
  return <View style={{ alignItems: 'center', paddingVertical: 72, paddingHorizontal: 24, gap: 14, borderWidth: 1, borderColor: palette.border, borderRadius: 18 }}>
    <View style={{ padding: 24, borderRadius: 22, backgroundColor: palette.preview }}><Icon name={icon} size={32} color={palette.icon} /></View>
    <Text size="xl" weight="medium" style={{ textAlign: 'center', marginTop: 4 }}>{title}</Text>
    <Text size="s" color="secondary" style={{ textAlign: 'center', maxWidth: 420, lineHeight: 21 }}>{description}</Text>
    <Action label={action} onPress={onAction} primary />
  </View>;
}

export default function FilesPage() {
  const { colors, theme } = useTheme();
  const palette = getLibraryPalette(theme);
  const { t } = useTranslation();
  const categories = FILE_CATEGORIES.map(item => ({ ...item, label: item.id === 'all' ? t('pages.common.allFiles') : ['documents', 'images', 'audio', 'code'].includes(item.id) ? t(`pages.files.${item.id}`) : item.label }));
  const sortOptions = SORT_OPTIONS.map((item, index) => ({ ...item, label: t(`pages.common.${['newestFirst', 'oldestFirst', 'nameAZ', 'nameZA'][index]}`) }));
  const { isXS } = useResponsiveness();
  const router = useRouter();
  const params = useLocalSearchParams();
  const insets = useSafeAreaInsets();
  const { isAuthResolved } = useHasUser();
  const token = useAuthStore(state => state.token);
  const isAuthed = Boolean(token);
  const emptyPreview = ['1', 'true', 'yes'].includes(String(params.empty || params.mockEmpty || '').toLowerCase());
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');
  const [source, setSource] = useState('all');
  const [sort, setSort] = useState('newest');
  const [view, setView] = useState('grid');
  const [contentWidth, setContentWidth] = useState(0);
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const [openingFileId, setOpeningFileId] = useState(null);
  const openRequest = useRef(0);
  useEffect(() => () => { openRequest.current += 1; }, []);
  const library = useFilesLibrary({ query, category, source, sort, enabled: isAuthed && !emptyPreview });
  const filesMap = useFileStore(state => state.files);
  const deleteFile = useFileStore(state => state.actions.deleteFile);
  const fetchCollections = useCollectionStore(state => state.fetchCollections);
  const openModal = useModalStore(state => state.actions.openModal);
  const addToast = useToastStore(state => state.actions.addToast);
  const openLightbox = useLightboxStore(state => state.actions.openLightbox);
  const initializeNewChatWithFiles = useChatInputStore(state => state.initializeNewChatWithFiles);
  const { useConfirmationModal } = useModalHelper();
  const confirm = useConfirmationModal();

  // The file action's collection picker reads the shared collection cache.
  useFocusEffect(useCallback(() => {
    if (isAuthed) void fetchCollections().catch(() => {});
  }, [fetchCollections, isAuthed]));

  // A changed scope must not leave invisible files selected for an action.
  useEffect(() => { setSelected(new Set()); }, [query, category, source, sort]);

  const files = useMemo(() => emptyPreview ? [] : library.ids.map(id => filesMap[id]).filter(Boolean), [library.ids, filesMap, emptyPreview]);
  const count = emptyPreview ? 0 : library.pagination.total || 0;
  const hasFilters = Boolean(query.trim() || category !== 'all' || source !== 'all');
  const gap = isXS ? 10 : 18;
  const columns = Math.max(1, Math.min(5, Math.floor((contentWidth + gap) / (isXS ? 166 : 218))));
  const cardWidth = Math.max(0, (contentWidth - (columns - 1) * gap) / columns);
  const compact = contentWidth < 680;
  const autoLoad = useFilesAutoLoad({
    enabled: isAuthed && !emptyPreview && files.length > 0 && library.pagination.hasMore
      && !library.loading && !library.loadingMore && !library.error,
    loadMore: library.loadMore,
  });

  const resetFilters = () => { setQuery(''); setCategory('all'); setSource('all'); };
  const details = useCallback(file => openModal('FileEditModal', { fileId: file.id }), [openModal]);
  const openFile = useCallback(async file => {
    const request = ++openRequest.current;
    const supported = isEditableDocumentFile(file) || isPresentationFile(file) || isSpreadsheetFile(file)
      || isEditorImportableFile(file) || isCodeFile(file) || isPdfFile(file) || isLightboxCompatibleFile(file);
    if (!supported || !file.cdnPath) { setOpeningFileId(null); details(file); return; }
    setOpeningFileId(file.id);
    try {
      // Match conversation attachments, including office import and image studio.
      await openMessageFile(file, { onOpenLightbox: openLightbox, throwOnError: true, shouldOpen: () => openRequest.current === request });
    } catch (error) {
      if (openRequest.current === request) addToast(error.message || 'Could not open this file. Please try again.', 'error');
    } finally { if (openRequest.current === request) setOpeningFileId(null); }
  }, [details, openLightbox, addToast]);
  const startChat = useCallback(file => {
    initializeNewChatWithFiles(file ? [file.id] : Array.from(selected));
    setSelected(new Set());
    router.push('/app');
  }, [selected, initializeNewChatWithFiles, router]);
  const collect = useCallback(file => {
    openModal('MoveToCollectionModal', { fileIds: file ? [file.id] : Array.from(selected), onSuccess: () => setSelected(new Set()) });
  }, [openModal, selected]);
  const download = useCallback(async file => {
    if (!file.cdnPath) return addToast('This file is not available to download yet.', 'error');
    try {
      await downloadMedia(file.cdnPath, file.name || 'download', { onSuccess: () => addToast('Download started', 'success'), onError: message => addToast(message || 'Download failed', 'error') });
    } catch (error) { addToast(error.message || 'Download failed', 'error'); }
  }, [addToast]);
  const toggleSelection = useCallback(file => {
    setSelected(previous => {
      const next = new Set(previous);
      if (next.has(file.id)) next.delete(file.id);
      else if (next.size < 10) next.add(file.id);
      return next;
    });
  }, []);
  const upload = useCallback(() => {
    // Uploading to the library should keep the user here, with their files visible.
    openModal('FileUploadModal', { libraryUpload: true, onUpload: result => {
      if (result.success) { resetFilters(); setSort('newest'); }
    } });
  }, [openModal]);
  const refresh = async () => {
    setRefreshing(true);
    try { await Promise.all([library.refresh(), isAuthed ? fetchCollections() : Promise.resolve()]); }
    finally { setRefreshing(false); }
  };
  const deleteSelected = () => confirm({
    title: `Delete ${selected.size === 1 ? 'file' : `${selected.size} files`}?`,
    message: 'These files will be removed from your library and collections. This cannot be undone.',
    confirmText: t('common.delete'), isDangerous: true,
    onConfirm: async () => {
      setDeleting(true);
      const failed = [];
      for (const id of selected) {
        try { await deleteFile(id); } catch (_) { failed.push(id); }
      }
      setDeleting(false);
      setSelected(new Set(failed));
      addToast(failed.length ? `${failed.length} file${failed.length === 1 ? '' : 's'} could not be deleted. Try again.` : 'Files deleted', failed.length ? 'error' : 'success');
      void library.refresh();
    },
  });

  return <View style={{ flex: 1, backgroundColor: colors.solid_color_primary }}>
    <MetaHead title={t('pages.files.title')} description={t('pages.files.description')} noindex />
    <ScrollView ref={autoLoad.scrollRef} onScroll={autoLoad.onScroll} onLayout={autoLoad.onLayout}
      onContentSizeChange={autoLoad.onContentSizeChange} scrollEventThrottle={100}
      style={{ flex: 1 }} contentContainerStyle={{ paddingBottom: selected.size ? 160 + insets.bottom : 40 + insets.bottom }}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={refresh} tintColor={colors.text_color_secondary} /> }>
      <View style={{ maxWidth: 1320, width: '100%', alignSelf: 'center', paddingHorizontal: isXS ? 16 : 40, paddingTop: isXS ? 16 : 32 }}>
        <View onLayout={event => setContentWidth(event.nativeEvent.layout.width)}>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 16, marginBottom: isXS ? 16 : 24 }}>
            <View style={{ flex: 1, minWidth: 140, gap: 6 }}>
              {!isXS && <Text tag="h1" size="xxxl" weight="bold" style={{ letterSpacing: -0.9, lineHeight: 38 }}>{t('pages.files.title')}</Text>}
              <Text size="s" color="secondary" style={{ lineHeight: 20 }}>A home for everything you create.</Text>
            </View>
            <Action label="Upload files" icon="arrow-up-to-line" onPress={upload} primary compact={isXS} />
          </View>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, minHeight: 44, paddingHorizontal: 14, marginBottom: isXS ? 16 : 24, borderRadius: 12, backgroundColor: palette.preview, borderWidth: 1, borderColor: searchFocused ? palette.activeBorder : 'transparent' }}>
            <Icon name="search" size={15} color={palette.icon} />
            <TextInput value={query} onChangeText={setQuery} accessibilityLabel={t('pages.files.searchPlaceholder')} placeholder={t('pages.files.searchPlaceholder')} placeholderTextColor={colors.text_color_secondary}
              autoCapitalize="none" autoCorrect={false} onFocus={() => setSearchFocused(true)} onBlur={() => setSearchFocused(false)}
              style={{ flex: 1, minWidth: 0, fontSize: 14, height: 42, color: colors.text_color_primary, ...(Platform.OS === 'web' && { outlineStyle: 'none' }) }} />
            {!!query && <Pressable accessibilityRole="button" accessibilityLabel="Clear search" onPress={() => setQuery('')} style={{ width: 32, height: 36, alignItems: 'center', justifyContent: 'center' }}><Icon name="circle-xmark" size={15} color={palette.icon} /></Pressable>}
          </View>
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <Text tag="h2" size="l" weight="bold">{query.trim() ? 'Search results' : categories.find(item => item.id === category).label}</Text>
              <View style={{ paddingHorizontal: 7, paddingVertical: 3, borderRadius: 6, backgroundColor: palette.preview }}>
                <Text size="xxs" color="secondary" accessibilityLiveRegion="polite">{library.loading ? '…' : count.toLocaleString()}</Text>
              </View>
              {hasFilters && <Pressable accessibilityRole="button" accessibilityLabel="Clear filters" onPress={resetFilters} style={{ padding: 8 }}><Text size="xs" color="secondary">Clear filters</Text></Pressable>}
            </View>
            <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
              <CatalogSortDropdown label="Source" value={source} options={SOURCE_OPTIONS} onChange={setSource} showLabel={false} />
              <CatalogSortDropdown label="Sort" value={sort} options={sortOptions} onChange={setSort} showLabel={false} />
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 2, padding: 3, borderRadius: 9, backgroundColor: palette.preview }}>
                {['grid', 'list'].map(mode => <Pressable key={mode} accessibilityRole="button" accessibilityLabel={`${mode === 'grid' ? 'Grid' : 'List'} view`} accessibilityState={{ selected: mode === view }} aria-pressed={mode === view} onPress={() => setView(mode)}
                  style={{ width: 32, height: 28, borderRadius: 6, alignItems: 'center', justifyContent: 'center', backgroundColor: view === mode ? palette.control : 'transparent' }}>
                  <Icon name={mode === 'grid' ? 'grid-2' : 'list'} size={13} color={view === mode ? colors.text_color_primary : palette.icon} />
                </Pressable>)}
              </View>
            </View>
          </View>
          <ScrollView horizontal accessibilityRole="tablist" accessibilityLabel="File types" showsHorizontalScrollIndicator={false} style={{ borderBottomWidth: 1, borderBottomColor: palette.border, marginBottom: 18 }} contentContainerStyle={{ gap: compact ? 18 : 24 }}>
            {categories.map(item => <Pressable key={item.id} accessibilityRole="tab" accessibilityLabel={item.label} accessibilityState={{ selected: category === item.id }} aria-selected={category === item.id} onPress={() => setCategory(item.id)}
              style={({ hovered }) => ({ minHeight: 42, justifyContent: 'center', borderBottomWidth: 2, borderBottomColor: category === item.id ? palette.ink : 'transparent', opacity: hovered ? 0.7 : 1 })}>
              <Text size="s" weight={category === item.id ? 'bold' : 'normal'} color={category === item.id ? 'primary' : 'secondary'}>{item.label}</Text>
            </Pressable>)}
          </ScrollView>
          {openingFileId && <View accessibilityLiveRegion="polite" style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 12 }}><ActivityIndicator size="small" color={colors.text_color_secondary} /><Text size="s" color="secondary">Opening file…</Text></View>}
          {library.loading ? <View accessibilityLiveRegion="polite" style={{ padding: 64, alignItems: 'center', gap: 14 }}><ActivityIndicator color={colors.text_color_secondary} /><Text size="s" color="secondary">Loading your files…</Text></View>
            : library.error && !files.length ? <EmptyResult icon="circle-exclamation" title="We couldn’t load your files" description={library.error} action="Try again" onAction={library.refresh} />
            : !files.length ? <EmptyResult icon={hasFilters ? 'search' : 'folder-open'} title={hasFilters ? 'No matching files' : 'Make room for your ideas'}
              description={hasFilters ? 'Try a different name, file type, or source.' : 'Upload a document, image, or anything you’re working on. Files you create in chat will appear here, too.'}
              action={hasFilters ? 'Clear filters' : 'Upload your first files'} onAction={hasFilters ? resetFilters : upload} />
            : <View style={view === 'grid' ? { flexDirection: 'row', flexWrap: 'wrap', columnGap: gap, rowGap: 22 } : { borderTopWidth: 1, borderColor: palette.border }}>
              {view === 'list' && !compact && <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, paddingVertical: 10, paddingLeft: 60, paddingRight: 60, borderBottomWidth: 1, borderColor: palette.border }}>
                <Text size="xxs" weight="medium" color="secondary" style={{ flex: 1 }}>Name</Text>
                <Text size="xxs" weight="medium" color="secondary" style={{ width: 70 }}>Kind</Text>
                <Text size="xxs" weight="medium" color="secondary" style={{ width: 98 }}>Date added</Text>
                <Text size="xxs" weight="medium" color="secondary" style={{ width: 72, textAlign: 'right' }}>Size</Text>
              </View>}
              {files.map(file => <LibraryFileItem key={file.id} file={file} width={cardWidth} list={view === 'list'} compact={compact}
                selected={selected.has(file.id)} selectionDisabled={selected.size >= 10 && !selected.has(file.id)} onSelect={toggleSelection}
                onOpen={openFile} onDetails={details} onDownload={download} onChat={startChat} onCollect={collect} />)}
            </View>}
          {!library.loading && files.length > 0 && <View ref={autoLoad.footerRef} style={{ alignItems: 'center', paddingTop: 28, gap: 14 }}>
            <View accessibilityLiveRegion="polite" style={{ minHeight: 36, alignItems: 'center', justifyContent: 'center', gap: 12 }}>
              {library.error ? <>
                <Text accessibilityRole="alert" size="s" style={{ color: colors.color_destructive }}>{library.error}</Text>
                <Action label="Try loading more again" onPress={library.loadMore} />
              </> : library.loadingMore ? <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <ActivityIndicator size="small" color={colors.text_color_secondary} />
                <Text size="xs" color="secondary">Loading more files…</Text>
              </View> : <Text size="xs" color="secondary">{library.pagination.hasMore
                ? `Showing ${files.length.toLocaleString()} of ${count.toLocaleString()} files`
                : `${count.toLocaleString()} ${count === 1 ? 'file' : 'files'}`}</Text>}
            </View>
          </View>}
          {Platform.OS === 'web' && !hasFilters && <View style={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center', gap: 7, paddingTop: 24 }}><Icon name="cloud-arrow-up" size={13} color={palette.icon} /><Text size="xs" color="secondary">Drop files anywhere to upload</Text></View>}
        </View>
      </View>
      {isAuthResolved && !isAuthed && <AppDashboardFooter fullViewportWidth />}
    </ScrollView>

    {selected.size > 0 && <View style={{ position: 'absolute', bottom: 20 + insets.bottom, width: isXS ? '94%' : '92%', maxWidth: 820, alignSelf: 'center', borderRadius: 16, borderWidth: 1, borderColor: palette.border, backgroundColor: palette.surface, padding: 14, gap: 12, flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', ...(Platform.OS === 'web' && { boxShadow: '0 8px 32px #00000024' }) }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
        <Pressable accessibilityRole="button" accessibilityLabel="Clear selection" onPress={() => setSelected(new Set())} style={{ padding: 8 }}><Icon name="xmark" size={14} color={colors.text_color_secondary} /></Pressable>
        <Text size="s" weight="medium">{selected.size} of 10 selected</Text>
      </View>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
        <Action label={t('pages.common.chat')} icon="message-dots" onPress={() => startChat()} primary disabled={deleting} />
        <Action label={t('dropdowns.addToCollection')} icon="folder-plus" onPress={() => collect()} disabled={deleting} compact />
        <Action label={deleting ? 'Deleting…' : t('common.delete')} icon="trash" onPress={deleteSelected} danger disabled={deleting} compact />
      </View>
    </View>}
  </View>;
}
